import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { keyframes } from '@mui/material/styles'

import { useQuestionDashboard } from '@/features/campaigns/api/question-dashboard-queries'
import { formatQuestionDrillDownLastUpdated } from '@/features/campaigns/lib/insight-display'
import { useDashboardLastUpdatedIso } from '@/features/campaigns/lib/use-dashboard-last-updated'
import { RiRefreshLine } from '@/shared/icons/remix'

const refreshSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

interface Props {
  instrumentId: string
  questionId: string
}

/** TC-QD-01 — last update + refresh in app header (status pill lives on the page title row). */
export function AppShellQuestionDrillDownStatus({
  instrumentId,
  questionId,
}: Readonly<Props>) {
  const { isFetching, refetch, dataUpdatedAt, data } = useQuestionDashboard(
    instrumentId,
    questionId,
  )
  const lastUpdatedIso = useDashboardLastUpdatedIso(
    data?.lastUpdatedAt,
    dataUpdatedAt,
    `${instrumentId}:${questionId}`,
  )

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
      <Typography
        data-testid="question-drilldown-last-updated"
        sx={{
          fontFamily: '"Inter", sans-serif',
          fontSize: '14px',
          lineHeight: '20px',
          letterSpacing: '-0.1504px',
          color: '#4A5565',
          whiteSpace: 'nowrap',
        }}
      >
        Last Update {formatQuestionDrillDownLastUpdated(lastUpdatedIso)}
      </Typography>
      <IconButton
        size="small"
        aria-label={isFetching ? 'Refreshing question dashboard' : 'Refresh question dashboard'}
        aria-busy={isFetching}
        data-testid="question-drilldown-refresh"
        disabled={isFetching}
        onClick={() => Promise.resolve(refetch()).catch(() => undefined)}
        sx={{
          width: 36,
          height: 36,
          color: '#4A5565',
          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
        }}
      >
        <Box
          component="span"
          aria-hidden
          sx={{
            display: 'inline-flex',
            animation: isFetching ? `${refreshSpin} 0.9s linear infinite` : 'none',
          }}
        >
          <RiRefreshLine size={24} />
        </Box>
      </IconButton>
    </Box>
  )
}
